import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, CheckCheck, Plus, Send, Trash2, Truck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/contexts/AuthContext";
import {
    extractError, PartAPI, PurchaseOrderAPI, SupplierAPI,
} from "@/lib/api";

// ---------- helpers
const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;
const STATUS_PILL = {
    draft: "border-zinc-300 bg-zinc-50 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    sent: "border-blue-300 bg-blue-50 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
    partially_received: "border-teal-400 bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
    received: "border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
    cancelled: "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/10 dark:text-brand-redSoft",
};

// ---------- List page
export default function PurchaseOrdersPage() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await PurchaseOrderAPI.list({
                status: status || undefined,
                page_size: 200,
            });
            setItems(d.items || []);
        } finally { setLoading(false); }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="purchase-orders-page">
            <header className="flex items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Procurement</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Purchase Orders</h1>
                    <p className="mt-1 text-sm text-zinc-500">Gapless per branch · weighted-average cost · atomic goods receipts.</p>
                </div>
                <Link to="/purchase-orders/new"><Button data-testid="po-new-btn"><Plus className="mr-1 h-4 w-4" />New PO</Button></Link>
            </header>

            <div className="flex items-center gap-2">
                <Select value={status} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="po-status-filter">
                        <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="partially_received">Partially received</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
                <span className="font-mono text-xs text-zinc-500">{loading ? "…" : `${items.length} POs`}</span>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="po-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>PO #</TableHead>
                            <TableHead>Supplier</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Lines</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead>Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((p) => (
                            <TableRow
                                key={p.id}
                                data-testid={`po-row-${p.id}`}
                                className="cursor-pointer"
                                onClick={() => (window.location.href = `/purchase-orders/${p.id}`)}
                            >
                                <TableCell className="font-mono">{p.po_number}</TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500">{p.supplier_id?.slice(-6)}</TableCell>
                                <TableCell>
                                    <span
                                        className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium font-mono ${STATUS_PILL[p.status] || ""}`}
                                        data-testid={`po-status-pill-${p.status}`}
                                    >
                                        {p.status.replace(/_/g, " ")}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">{p.lines?.length || 0}</TableCell>
                                <TableCell className="text-right font-mono">{inr(p.total_minor)}</TableCell>
                                <TableCell className="text-xs font-mono text-zinc-500">
                                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No purchase orders.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ---------- New PO wizard
export function PurchaseOrderNewPage() {
    const nav = useNavigate();
    const { currentBranchId, branches } = useAuth();
    const [params] = useSearchParams();

    const hint_supplier = params.get("supplier_hint") || "";
    const hint_branch = params.get("branch_id") || currentBranchId || "";
    const hint_part = params.get("part_id") || "";

    const [branchId, setBranchId] = useState(hint_branch);
    const [supplierId, setSupplierId] = useState(hint_supplier);
    const [suppliers, setSuppliers] = useState([]);
    const [parts, setParts] = useState([]);
    const [lines, setLines] = useState([]);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        SupplierAPI.list({ is_active: true, page_size: 200 }).then((d) => setSuppliers(d.items || []));
        PartAPI.list({ is_active: true, page_size: 500 }).then((d) => setParts(d.items || []));
    }, []);

    useEffect(() => {
        if (hint_part && parts.length && !lines.length) {
            const p = parts.find((x) => x.id === hint_part);
            if (p) setLines([{ part_id: p.id, part_number: p.part_number, name: p.name, quantity: 1, unit_cost_minor: p.unit_cost_minor, tax_rate_percent: p.tax_rate_percent }]);
        }
    }, [hint_part, parts]); // eslint-disable-line

    const addLine = () => setLines([...lines, { part_id: "", quantity: 1, unit_cost_minor: 0, tax_rate_percent: 18 }]);
    const setLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

    const subtotal = useMemo(
        () => lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_cost_minor) || 0), 0),
        [lines],
    );
    const tax = useMemo(
        () => lines.reduce((acc, l) => acc + Math.floor((Number(l.quantity) || 0) * (Number(l.unit_cost_minor) || 0) * (Number(l.tax_rate_percent) || 0) / 100), 0),
        [lines],
    );

    const submit = async () => {
        if (!branchId || !supplierId || lines.length === 0) {
            toast.error("Branch, supplier and at least one line are required");
            return;
        }
        if (lines.some((l) => !l.part_id || !l.quantity || l.unit_cost_minor == null)) {
            toast.error("Every line needs a part, quantity and unit cost");
            return;
        }
        setSubmitting(true);
        try {
            const created = await PurchaseOrderAPI.create({
                branch_id: branchId, supplier_id: supplierId, notes: notes || null,
                lines: lines.map((l) => ({
                    part_id: l.part_id, quantity: Number(l.quantity),
                    unit_cost_minor: Number(l.unit_cost_minor),
                    tax_rate_percent: Number(l.tax_rate_percent || 0),
                })),
            });
            toast.success(`Created PO ${created.po_number}`);
            nav(`/purchase-orders/${created.id}`);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="mx-auto max-w-4xl space-y-6" data-testid="po-new-page">
            <Link to="/purchase-orders" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back to POs
            </Link>
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">New Purchase Order</h1>
                <p className="mt-1 text-sm text-zinc-500">Draft now, send when ready. PO numbers are gapless per branch per FY.</p>
            </header>
            <Card>
                <CardContent className="space-y-5 p-5">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Branch</Label>
                            <Select value={branchId} onValueChange={setBranchId}>
                                <SelectTrigger data-testid="po-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                                <SelectContent>
                                    {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Supplier</Label>
                            <Select value={supplierId} onValueChange={setSupplierId}>
                                <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                                <SelectContent>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.supplier_code} — {s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <Label>Lines</Label>
                            <Button size="sm" variant="outline" onClick={addLine} data-testid="po-add-line-btn"><Plus className="mr-1 h-3 w-3" />Add line</Button>
                        </div>
                        <div className="space-y-2">
                            {lines.map((ln, idx) => (
                                <div key={idx} className="grid grid-cols-12 items-end gap-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800" data-testid={`po-new-line-${idx}`}>
                                    <div className="col-span-5">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Part</Label>
                                        <Select value={ln.part_id} onValueChange={(v) => {
                                            const p = parts.find((x) => x.id === v);
                                            setLine(idx, { part_id: v, unit_cost_minor: p?.unit_cost_minor ?? ln.unit_cost_minor, tax_rate_percent: p?.tax_rate_percent ?? ln.tax_rate_percent });
                                        }}>
                                            <SelectTrigger data-testid={`po-new-line-${idx}-part-select`}><SelectValue placeholder="Select part" /></SelectTrigger>
                                            <SelectContent className="max-h-[300px]">
                                                {parts.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        <span className="font-mono text-xs text-zinc-500">{p.part_number}</span>&nbsp;{p.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Qty</Label>
                                        <Input type="number" value={ln.quantity} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) })} data-testid={`po-new-line-${idx}-qty`} />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Unit cost (minor)</Label>
                                        <Input type="number" value={ln.unit_cost_minor} onChange={(e) => setLine(idx, { unit_cost_minor: Number(e.target.value) })} data-testid={`po-new-line-${idx}-cost`} />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Tax %</Label>
                                        <Input type="number" value={ln.tax_rate_percent} onChange={(e) => setLine(idx, { tax_rate_percent: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} data-testid={`po-new-line-${idx}-remove`}>
                                            <Trash2 className="h-4 w-4 text-brand-red" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {lines.length === 0 && (
                                <div className="rounded-md border border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-500 dark:border-zinc-800">
                                    No lines yet. Click "Add line" to start.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Notes</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>

                    <div className="flex items-center justify-between border-t border-zinc-200 pt-3 text-sm font-mono dark:border-zinc-800">
                        <div className="text-zinc-500">{lines.length} line{lines.length !== 1 ? "s" : ""}</div>
                        <div className="space-x-3">
                            <span className="text-zinc-500">Subtotal {inr(subtotal)}</span>
                            <span className="text-zinc-500">Tax {inr(tax)}</span>
                            <span className="text-base font-bold text-zinc-900 dark:text-zinc-100" data-testid="po-new-total">Total {inr(subtotal + tax)}</span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => nav("/purchase-orders")}>Cancel</Button>
                        <Button onClick={submit} disabled={submitting} data-testid="po-new-submit">
                            {submitting ? "Creating…" : "Save draft"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ---------- Detail page (with Receive Goods dialog)
export function PurchaseOrderDetailPage() {
    const { id } = useParams();
    const [po, setPo] = useState(null);
    const [receiveOpen, setReceiveOpen] = useState(false);

    const load = useCallback(async () => {
        setPo(await PurchaseOrderAPI.get(id));
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (!po) return <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>;

    const canSend = po.status === "draft";
    const canCancel = po.status === "draft" || po.status === "sent";
    const canReceive = po.status === "sent" || po.status === "partially_received";

    const act = async (fn, label) => {
        try { await fn(); toast.success(`${label} OK`); load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="po-detail-page">
            <Link to="/purchase-orders" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back
            </Link>
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Purchase order</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="po-number-heading">{po.po_number}</h1>
                    <div className="mt-1 text-sm text-zinc-500">
                        {po.supplier?.name} · {po.branch?.code} · v{po.version}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        data-testid="po-status-pill"
                        className={`inline-flex rounded-md border px-3 py-1 text-sm font-medium font-mono ${STATUS_PILL[po.status] || ""}`}
                    >
                        <span data-testid={`po-status-pill-${po.status}`}>{po.status.replace(/_/g, " ")}</span>
                    </span>
                    {canSend && <Button onClick={() => act(() => PurchaseOrderAPI.send(po.id), "Sent")} data-testid="po-action-send"><Send className="mr-1 h-3.5 w-3.5" />Send</Button>}
                    {canReceive && <Button onClick={() => setReceiveOpen(true)} data-testid="po-action-receive"><Truck className="mr-1 h-3.5 w-3.5" />Receive goods</Button>}
                    {canCancel && <Button variant="outline" onClick={() => act(() => PurchaseOrderAPI.cancel(po.id), "Cancelled")} data-testid="po-action-cancel"><Ban className="mr-1 h-3.5 w-3.5" />Cancel</Button>}
                </div>
            </header>

            <Card>
                <CardHeader><CardTitle>Lines</CardTitle></CardHeader>
                <CardContent className="p-0">
                    <Table data-testid="po-lines-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Part</TableHead>
                                <TableHead className="text-right">Ordered</TableHead>
                                <TableHead className="text-right">Received</TableHead>
                                <TableHead className="text-right">Rejected</TableHead>
                                <TableHead className="text-right">Unit cost</TableHead>
                                <TableHead className="text-right">Tax %</TableHead>
                                <TableHead className="text-right">Line total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {po.lines.map((l) => (
                                <TableRow key={l.id} data-testid={`po-line-${l.id}`}>
                                    <TableCell>
                                        <div className="font-mono text-xs text-zinc-500">{l.part_number}</div>
                                        <div className="font-medium">{l.part_name}</div>
                                    </TableCell>
                                    <TableCell className="text-right font-mono">{l.quantity}</TableCell>
                                    <TableCell className="text-right font-mono" data-testid={`po-line-${l.id}-received`}>{l.received_quantity || 0}</TableCell>
                                    <TableCell className="text-right font-mono">{l.rejected_quantity || 0}</TableCell>
                                    <TableCell className="text-right font-mono">{inr(l.unit_cost_minor)}</TableCell>
                                    <TableCell className="text-right font-mono">{l.tax_rate_percent}%</TableCell>
                                    <TableCell className="text-right font-mono">{inr(l.quantity * l.unit_cost_minor)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>Totals</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-3 gap-4 text-sm">
                    <KV label="Subtotal" value={inr(po.subtotal_minor)} />
                    <KV label="Tax" value={inr(po.tax_minor)} />
                    <KV label="Total" value={<span className="text-lg font-bold">{inr(po.total_minor)}</span>} />
                </CardContent>
            </Card>

            {po.receipts && po.receipts.length > 0 && (
                <Card>
                    <CardHeader><CardTitle>Goods receipts</CardTitle></CardHeader>
                    <CardContent>
                        <ul className="space-y-2 text-sm">
                            {po.receipts.map((g) => (
                                <li key={g.id} className="flex items-center justify-between rounded-md border border-zinc-200 p-2 dark:border-zinc-800" data-testid={`grn-row-${g.id}`}>
                                    <div>
                                        <div className="font-mono text-xs">{g.grn_number}</div>
                                        <div className="text-xs text-zinc-500">{new Date(g.created_at).toLocaleString()}</div>
                                    </div>
                                    <div className="text-right font-mono text-xs">
                                        <div>{g.lines?.length} line(s)</div>
                                        <div className="text-zinc-500">{inr(g.received_total_minor)}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            <ReceiveDialog po={po} open={receiveOpen} onOpenChange={setReceiveOpen} onDone={load} />
        </div>
    );
}

function ReceiveDialog({ po, open, onOpenChange, onDone }) {
    const [rows, setRows] = useState([]);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (open && po) {
            setRows(po.lines.map((l) => ({
                po_line_id: l.id, part_number: l.part_number, part_name: l.part_name,
                outstanding: Number(l.quantity) - Number(l.received_quantity || 0),
                quantity_received: Math.max(0, Number(l.quantity) - Number(l.received_quantity || 0)),
                rejected_quantity: 0,
            })));
            setNote("");
        }
    }, [open, po]);

    const submit = async () => {
        const payload_lines = rows
            .filter((r) => r.quantity_received > 0 || r.rejected_quantity > 0)
            .map((r) => ({
                po_line_id: r.po_line_id,
                quantity_received: Number(r.quantity_received),
                rejected_quantity: Number(r.rejected_quantity || 0),
            }));
        if (payload_lines.length === 0) { toast.error("Enter at least one line quantity"); return; }
        setSubmitting(true);
        try {
            const res = await PurchaseOrderAPI.receive(po.id, { lines: payload_lines, notes: note || null });
            toast.success(`Goods received — PO now ${res.po_status_after.replace(/_/g, " ")}`);
            onOpenChange(false);
            onDone && onDone();
        } catch (e) {
            toast.error(extractError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="grn-dialog">
                <DialogHeader>
                    <DialogTitle>Receive goods — {po?.po_number}</DialogTitle>
                    <DialogDescription>One atomic transaction. Stock + cost + PO status all flip together.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-[420px] overflow-y-auto">
                    {rows.map((r, i) => (
                        <div key={r.po_line_id} className="grid grid-cols-12 items-end gap-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800">
                            <div className="col-span-6">
                                <div className="font-mono text-xs text-zinc-500">{r.part_number}</div>
                                <div className="text-sm">{r.part_name}</div>
                                <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400">Outstanding {r.outstanding}</div>
                            </div>
                            <div className="col-span-3">
                                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Received</Label>
                                <Input
                                    type="number" min={0} max={r.outstanding}
                                    value={r.quantity_received}
                                    onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, quantity_received: Number(e.target.value) } : x))}
                                    data-testid={`grn-line-${r.po_line_id}-qty`}
                                />
                            </div>
                            <div className="col-span-3">
                                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Rejected</Label>
                                <Input
                                    type="number" min={0} max={r.outstanding}
                                    value={r.rejected_quantity || 0}
                                    onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, rejected_quantity: Number(e.target.value) } : x))}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="space-y-1.5">
                    <Label>Delivery note ref / notes</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="grn-submit">
                        {submitting ? "Receiving…" : "Receive"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function KV({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
            <div className="mt-0.5 font-mono">{value}</div>
        </div>
    );
}
